import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { v4 } from 'uuid';
import * as Yup from 'yup';
import { DB } from '~/services/firebase';

import { Status } from '~/components/Status';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';
import IDistance from '~/interfaces/IDistance';
import Dashboard from '~/layouts/Dashboard';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import getValidationErrors from '~/utils/getValidationErrors';

interface FormData {
  type: string;
  district: string;
  from: string;
  to: string;
  value: string;
  limit: number;
  observations: string;
}

const DistancesForm: React.FC = () => {
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const [distances, setDistances] = useState<IDistance[]>([]);
  const [data, setData] = useState<FormData>({ type: '1' } as FormData);

  const [editData, setEditData] = useState({} as IDistance);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);

  const load = useCallback(async () => {
    const q = query(
      collection(DB, 'distances'),
      where('user_id', '==', user.uid),
    );
    const querySnapshot = await getDocs(q);

    const items: IDistance[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as IDistance;

      items.push(parseItem);
    });

    items.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));

    // items.sort((a, b) => (a.from > b.from ? 1 : b.from > a.from ? -1 : 0));

    setDistances(items);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirmRemove = useCallback(
    async (uid: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do item?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        const items = [...distances];
        const findIndex = items.findIndex(find => find.uid === uid);

        if (findIndex !== -1) {
          await deleteDoc(doc(DB, 'distances', uid));

          items.splice(findIndex, 1);

          setDistances(items);

          addToast({
            type: 'success',
            description: 'A distância foi removida com sucesso.',
          });
        }
      }
    },
    [addToast, distances],
  );

  // const handleSubmit = useCallback(
  //   async event => {
  //     event.preventDefault();

  //     try {
  //       const schema = Yup.object().shape({
  //         name: Yup.string().required('O nome é obrigatório'),
  //       });

  //       await schema.validate(data, {
  //         abortEarly: false,
  //       });

  //       setLoading(true);

  //       const { name, status, phone, email, vehicle_plate } = data;

  //       const inputFile = document.getElementById('photo') as HTMLInputElement;

  //       let image = '';

  //       if (inputFile && inputFile.files && inputFile.files.length > 0) {
  //         const file = inputFile.files[0];
  //         const storageRef = Storage.ref(`deliverymen/${v4()}.png`);
  //         const resposne = await storageRef.put(file);
  //         image = await resposne.ref.getDownloadURL();
  //       }

  //       const uid = data.uid || v4();

  //       let parseData: any = {
  //         name,
  //         user_id: user.uid,
  //         company_id: company.uid,
  //         status,
  //         phone,
  //         email,
  //         vehicle_plate,
  //       };

  //       if (image) parseData = { image, ...parseData };

  //       if (data.uid) {
  //         await updateDoc(doc(DB, 'deliverymen', uid), parseData);
  //       } else {
  //         await setDoc(doc(DB, 'deliverymen', uid), parseData);
  //       }

  //       addToast({
  //         type: 'success',
  //         description: data.uid
  //           ? 'Entregador alterado com sucesso!'
  //           : 'Entregador criado com sucesso!',
  //       });

  //       history.push('/deliverymen');

  //       setLoading(false);
  //     } catch (err: any) {
  //       console.log(err);
  //       setLoading(false);
  //       if (err instanceof Yup.ValidationError) {
  //         const parseErrors = getValidationErrors(err);
  //         setErrors(parseErrors);
  //       }
  //     }
  //   },
  //   [user, company, data, addToast, history],
  // );

  const handleChangeStatus = useCallback(
    async (index: number) => {
      const items = [...distances];

      if (items[index].status === false) {
        items[index].status = true;
      } else {
        items[index].status = false;
      }

      await updateDoc(doc(DB, 'distances', items[index].uid), {
        status: items[index].status,
      });

      setDistances(items);
    },
    [distances],
  );

  const handleUpdateValue = useCallback(async () => {
    try {
      const items = [...distances];
      const findIndex = items.findIndex(item => item.uid === editData.uid);

      if (findIndex !== -1) {
        const updateData: any = {
          value: editData.value,
          observations: editData.observations || '',
        };

        // Add name fields based on type
        if (editData.type === '1' || !editData.type) {
          updateData.district = editData.district;
        } else if (editData.type === '0') {
          updateData.from = editData.from;
          updateData.to = editData.to;
        }

        await updateDoc(doc(DB, 'distances', editData.uid), updateData);

        // Update local state
        items[findIndex].value = editData.value;
        items[findIndex].observations = editData.observations || '';
        if (editData.type === '1' || !editData.type) {
          items[findIndex].district = editData.district;
        } else if (editData.type === '0') {
          items[findIndex].from = editData.from;
          items[findIndex].to = editData.to;
        }
        setDistances(items);
      }

      addToast({
        type: 'success',
        description: 'Localidade atualizada com sucesso!',
      });

      setEditData({} as IDistance);
    } catch (err) {
      console.log(err);
    }
  }, [editData, addToast, distances]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          type: Yup.string().required('O campo tipo é obrigatório'),
          district:
            data.type === '1'
              ? Yup.string().required('O campo bairro é obrigatório')
              : Yup.string(),
          from:
            data.type === '0'
              ? Yup.string().required('O campo de é obrigatório')
              : Yup.string(),
          to:
            data.type === '0'
              ? Yup.string().required('O campo até é obrigatório')
              : Yup.string(),
          value: Yup.string().required('O valor é obrigatório'),
          observations: Yup.string(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const uid = v4();

        const parseData: any = {
          ...data,
          uid,
          from: parseFloat(data.from || '0'),
          to: parseFloat(data.to || '0'),
          value: parseFloat(data.value),
          observations: data.observations || '',
          user_id: user.uid,
          company_id: company.uid,
          status: true,
        };

        await setDoc(doc(DB, 'distances', uid), parseData);

        const items = [parseData, ...distances];

        items.sort((a, b) => (a.from > b.from ? 1 : b.from > a.from ? -1 : 0));

        setDistances(items);

        setData({ type: data.type } as FormData);

        if (data.type === '0') {
          (document.getElementById('from') as any).value = '';
          (document.getElementById('to') as any).value = '';
        }

        (document.getElementById('value') as any).value = '';
        (document.getElementById('observations') as any).value = '';

        addToast({
          type: 'success',
          description: 'Configuração cadastrada com sucesso!',
        });

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, company, addToast, data, distances],
  );

  return (
    <Dashboard containerMessage="col-xl-8 offset-xl-2 mb-4">
      <div className="row">
        <div className="col-xl-8 offset-xl-2 mb-4">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/categories">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">Configurar Entrega</h4>
                <div className="nk-block-des">
                  <p>Configure o valor da entrega de acordo com a distância.</p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-3">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Tipo de Configuração
                        </label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              id="category"
                              value={data.type}
                              onChange={event => {
                                setData({
                                  ...data,
                                  type: event.target.value,
                                });
                              }}
                            >
                              <option value="0">Distância</option>
                              <option value="1">Bairro</option>
                            </select>
                            {errors.category_id && (
                              <span className="invalid">
                                {errors.category_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {data.type === '0' && (
                      <>
                        <div className="col-lg-3">
                          <div className="form-group mb-3">
                            <label className="form-label">De (km)</label>
                            <div className="form-control-wrap">
                              <input
                                id="from"
                                type="number"
                                step=".01"
                                className="form-control"
                                defaultValue={data.from}
                                onChange={event => {
                                  setData({
                                    ...data,
                                    from: event.target.value,
                                  });
                                }}
                              />
                              {errors.from && (
                                <span className="invalid">{errors.from}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3">
                          <div className="form-group mb-3">
                            <label className="form-label">Até (km)</label>
                            <div className="form-control-wrap">
                              <input
                                id="to"
                                type="number"
                                step=".01"
                                className="form-control"
                                defaultValue={data.to}
                                onChange={event => {
                                  setData({ ...data, to: event.target.value });
                                }}
                              />
                              {errors.to && (
                                <span className="invalid">{errors.to}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {data.type === '1' && (
                      <div className="col-lg-6">
                        <div className="form-group mb-3">
                          <label className="form-label">Nome do bairro</label>
                          <div className="form-control-wrap">
                            <input
                              id="district"
                              type="text"
                              className="form-control"
                              defaultValue={data.district}
                              onChange={event => {
                                setData({
                                  ...data,
                                  district: event.target.value,
                                });
                              }}
                            />
                            {errors.district && (
                              <span className="invalid">{errors.district}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="col-lg-3">
                      <div className="form-group mb-3">
                        <label className="form-label">Valor (R$)</label>
                        <div className="form-control-wrap">
                          <input
                            id="value"
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.value}
                            onChange={event => {
                              setData({ ...data, value: event.target.value });
                            }}
                          />
                          {errors.value && (
                            <span className="invalid">{errors.value}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label className="form-label">Observações</label>
                        <div className="form-control-wrap">
                          <input
                            id="observations"
                            type="text"
                            className="form-control"
                            defaultValue={data.observations}
                            onChange={event => {
                              setData({ ...data, observations: event.target.value });
                            }}
                            placeholder="Avisos de limitações de entregas"
                          />
                          {errors.observations && (
                            <span className="invalid">{errors.observations}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      className="col-sm-12"
                      style={{ alignItems: 'flex-end' }}
                    >
                      <div className="form-group mb-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Adicionar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-8 offset-xl-2">
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between">
              <div className="nk-block-head-content">
                <h6 className="nk-block-title">Localidades</h6>
              </div>
            </div>
          </div>
          <div className="nk-block">
            <div className="card card-bordered">
              <div className="card-inner-group">
                <div className="card-inner p-0">
                  <div className="nk-tb-list">
                    <div className="nk-tb-item nk-tb-head">
                      <div className="nk-tb-col">
                        <span>Tipo</span>
                      </div>
                      <div className="nk-tb-col">
                        <span>Distância/Bairro</span>
                      </div>
                      <div className="nk-tb-col">
                        <span>Valor</span>
                      </div>
                      <div className="nk-tb-col">
                        <span>Observações</span>
                      </div>

                      <div className="nk-tb-col">
                        <span>Status</span>
                      </div>

                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1" />
                        </ul>
                      </div>
                    </div>

                    {distances.map((item, index) => (
                      <div className="nk-tb-item" key={item.uid}>
                        <div className="nk-tb-col">
                          <span className="tb-product">
                            {(item.type === '0' || !item.type) && 'Distância'}
                            {item.type === '1' && 'Bairro'}
                          </span>
                        </div>
                        <div className="nk-tb-col">
                          <span className="tb-product">
                            {(item.type === '0' || !item.type) &&
                              `${item.from} - ${item.to}km`}
                            {item.type === '1' && item.district}
                          </span>
                        </div>
                        <div className="nk-tb-col">
                          <span className="tb-product">
                            {getCurrencyFormatted(item.value)}
                          </span>
                        </div>
                        <div className="nk-tb-col">
                          <span className="tb-product">
                            {item.observations || '-'}
                          </span>
                        </div>
                        <div className="nk-tb-col">
                          <Status
                            value={!!item.status}
                            onChange={() => handleChangeStatus(index)}
                          />
                        </div>

                        <div className="nk-tb-col nk-tb-col-tools">
                          <ul className="nk-tb-actions gx-1 my-n1">
                            <li className="mr-n1">
                              <div className="dropdown">
                                <Link
                                  to="#options"
                                  className="dropdown-toggle btn btn-icon btn-trigger"
                                  data-toggle="dropdown"
                                >
                                  <em className="icon ni ni-more-h" />
                                </Link>
                                <div className="dropdown-menu dropdown-menu-right">
                                  <ul className="link-list-opt no-bdr">
                                    <li>
                                      <Link
                                        to="#edit"
                                        onClick={() => {
                                          setEditData(item);
                                        }}
                                      >
                                        <em className="icon ni ni-edit" />
                                        <span>Editar</span>
                                      </Link>
                                    </li>
                                    <li>
                                      <Link
                                        to="#remove"
                                        onClick={() => {
                                          handleConfirmRemove(item.uid);
                                        }}
                                      >
                                        <em className="icon ni ni-trash" />
                                        <span>Excluir</span>
                                      </Link>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {editData.uid && (
        <>
          <div
            className="modal fade show"
            id="editTaxaModal"
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'block' }}
          >
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content product border-0 shadow-sm">
                <div className="modal-body">
                  <h6
                    className="text-color-theme mb-3 text-center"
                    style={{
                      color: company.app_color,
                      textTransform: 'uppercase',
                    }}
                  >
                    Editar Localidade
                  </h6>

                  <form>
                    {(editData.type === '1' || !editData.type) && (
                      <div className="col-12 mb-4">
                        <div className="form-group">
                          <label className="form-label">Nome do bairro</label>
                          <div className="form-control-wrap">
                            <input
                              id="district"
                              type="text"
                              className="form-control"
                              defaultValue={editData.district}
                              onChange={event => {
                                setEditData({
                                  ...editData,
                                  district: event.target.value,
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {(editData.type === '0') && (
                      <>
                        <div className="col-12 mb-4">
                          <div className="form-group">
                            <label className="form-label">De (km)</label>
                            <div className="form-control-wrap">
                              <input
                                id="from"
                                type="number"
                                step=".01"
                                className="form-control"
                                defaultValue={editData.from}
                                onChange={event => {
                                  setEditData({
                                    ...editData,
                                    from: parseFloat(event.target.value),
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-12 mb-4">
                          <div className="form-group">
                            <label className="form-label">Até (km)</label>
                            <div className="form-control-wrap">
                              <input
                                id="to"
                                type="number"
                                step=".01"
                                className="form-control"
                                defaultValue={editData.to}
                                onChange={event => {
                                  setEditData({
                                    ...editData,
                                    to: parseFloat(event.target.value),
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="col-12 mb-4">
                      <div className="form-group">
                        <label className="form-label">Valor (R$)</label>
                        <div className="form-control-wrap">
                          <input
                            id="value"
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={editData.value}
                            onChange={event => {
                              setEditData({
                                ...editData,
                                value: parseFloat(event.target.value),
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 mb-4">
                      <div className="form-group">
                        <label className="form-label">Observações</label>
                        <div className="form-control-wrap">
                          <input
                            id="observations"
                            type="text"
                            className="form-control"
                            defaultValue={editData.observations || ''}
                            onChange={event => {
                              setEditData({
                                ...editData,
                                observations: event.target.value,
                              });
                            }}
                            placeholder="Avisos de limitações de entregas"
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="modal-footer justify-content-center">
                  <button
                    type="button"
                    className="btn btn-link text-color-theme"
                    data-bs-dismiss="modal"
                    style={{ color: company.app_color }}
                    onClick={handleUpdateValue}
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>
            <div
              className="modal-backdrop fade show"
              style={{ background: '#00000060' }}
              onClick={() => {
                setEditData({} as IDistance);
              }}
            />
          </div>
        </>
      )}
    </Dashboard>
  );
};

export default DistancesForm;
